import { hubName } from "./hubs";
import { CHEAT_DETAILS, CHEAT_LABELS, CHEATS, type CheatId } from "./cheats";
import type { InputFrame } from "./input";
import type { Tier } from "./ladder";
import { MAP_CATALOG } from "./maps";
import { HILL_SITE } from "./site";
import { SKATERS } from "./skaters";
import { ACTION_LABELS, CHOICE_KEYS, isListKey, REMAPPABLE, SETTING_CHOICES, SETTING_ROWS, type Action, type ArtStyleId, type ChoiceKey, type ListKey, type PickAction, type Quality, type SettingGroup, type StepKey } from "./settings";
import { STAT_IDS, STAT_INFO, type StatId } from "./stats";
import { RIDES } from "./sim/rides";
import { RIDE_SPECIALS } from "./special-seqs";
import { dayHeading, RELEASE_NOTES } from "./release-notes";

// The pause menu's state and controller navigation. Kept free of rendering so
// it can be tested; Game runs the actions and React draws it.

/**
 * Settings (#126) opens on a short list of screens: a `SettingsScreen`
 * each, then Cheats. A `ListKey` screen (art style, a trick effect...) is
 * one of their pick lists: every choice at once, so moving through them
 * rebuilds nothing; only picking one applies it.
 */
export type PauseScreen = "main" | "maps" | "gaps" | "goals" | "stats" | "skater" | "settings" | SettingsScreen | ListKey | "cheats" | "specials" | "news";

/** Settings' screens: the `SETTING_ROWS` groups, and Controls (`input`: touch rows, bindings). */
export type SettingsScreen = SettingGroup | "input";
export const SETTINGS_SCREENS: readonly SettingsScreen[] = ["look", "effects", "soundCamera", "input"];
export const SETTINGS_SCREEN_LABELS: Record<SettingsScreen, string> = { look: "Look", effects: "Effects", soundCamera: "Ride, sound & camera", input: "Controls" };

export function isSettingsScreen(v: string): v is SettingsScreen {
  return (SETTINGS_SCREENS as readonly string[]).includes(v);
}

/**
 * Which control rows apply on this device (#126): the touch rows where
 * there's a touchscreen (or the touch controls were forced on), the button
 * bindings and Reset controls where there's a keyboard or a pad.
 */
export interface ControlRows {
  touch: boolean;
  buttons: boolean;
}
export const ALL_CONTROL_ROWS: ControlRows = { touch: true, buttons: true };

export type PauseAction =
  | "resume"
  | "reset"
  | "maps"
  | "gaps"
  | "goals"
  | "run"
  | "controls"
  | "demo"
  | "settings"
  | "stats"
  | "skater"
  | "retry"
  | "quit"
  | "back"
  /** Back to the lobby (#224): ends the run, challenge or demo and leaves the map. */
  | "lobby"
  /** What's new (#159): its screen, and a row per day of notes (X on one does nothing; the rows let the stick scroll). */
  | "news"
  | `news:${string}`
  | `map:${string}`
  /** Pause > Challenges' "Go to hub" row for an open tier (#87). */
  | `hub:${Tier}`
  /** Ride this skater (#64). */
  | `skater:${string}`
  /** Spend a stat point on this stat (X or right on its row), or take one back (left). */
  | `stat:${StatId}`
  | `unstat:${StatId}`
  /** Settings' rows: each opens its screen (#126). */
  | `group:${SettingsScreen}`
  /** Settings rows: X (or ←/→) steps the volume; X on a binding waits for the new button or key. */
  | "volume+"
  | "volume-"
  /** A stepped pick-one row (`SETTING_ROWS`: the camera): X or ←/→ steps it. */
  | `${StepKey}+`
  | `${StepKey}-`
  /**
   * A list row (the art style and quality, #75; the trick effects, #126)
   * opens its pick list; a pick there applies that choice (for the look, one
   * rebuild of the park and rider) and goes back to its screen.
   */
  | ListKey
  | PickAction
  /** The Cheats row (#119) opens their list, where X on a cheat turns it on or off. */
  | "cheats"
  | `cheat:${CheatId}`
  /** The touch controls' rows (#67): mode, size and opacity step; X or ←/→ on the hand row flips it. */
  | "touchMode+"
  | "touchMode-"
  | "touchSize+"
  | "touchSize-"
  | "touchSteer+"
  | "touchSteer-"
  | "touchOpacity+"
  | "touchOpacity-"
  | "touchHand"
  | `bind:${Action}`
  | "resetControls"
  /**
   * Settings > Controls > Specials (#135): its screen; a special's row (X
   * waits for two stick taps, or steps on a touchscreen without keys or
   * pad), ←/→ steps it through the free sequences; Reset specials.
   */
  | "specials"
  | `special:${string}`
  | `special+:${string}`
  | `special-:${string}`
  | "resetSpecials";

export interface PauseItem {
  action: PauseAction;
  label: string;
  /** A line under the label (map descriptions). */
  detail?: string;
}

export const PAUSE_ITEMS: PauseItem[] = [
  { action: "resume", label: "Resume" },
  { action: "reset", label: "Reset skater" },
  // Reads "End run" during a run (PauseOverlay swaps the label).
  { action: "run", label: "2-minute run" },
  { action: "goals", label: "Goals" },
  // Says how many points there are to spend (PauseOverlay adds it).
  { action: "stats", label: "Stats" },
  // Who you're riding (#64): the select screen, with the current one marked.
  { action: "skater", label: "Skater" },
  { action: "maps", label: "Change map" },
  { action: "gaps", label: "Gap list" },
  { action: "settings", label: "Settings" },
  { action: "controls", label: "Controls" },
  // Reads "Stop demo" while one is playing (PauseOverlay swaps the label).
  { action: "demo", label: "Watch demo" },
  // A dot while there are notes the player hasn't seen (PauseOverlay adds it).
  { action: "news", label: "What's new" },
  { action: "lobby", label: "Back to lobby" },
];

/**
 * The hill site's main menu (#219): only what means something on the hill.
 * No runs, challenges, stats (their points come from challenges), maps, gap
 * list, demo or What's new: they're all the parks'.
 */
export const HILL_SITE_ROWS: readonly PauseAction[] = ["resume", "reset", "skater", "settings", "controls", "lobby"];

/** The main menu's rows on this site: all of them, or the hill site's (#219). */
export function siteItems(items: readonly PauseItem[], hillSite = HILL_SITE): PauseItem[] {
  return hillSite ? items.filter((i) => HILL_SITE_ROWS.includes(i.action)) : [...items];
}

/** The cheats on this site: Mirror mode flips a park, so the hill site (#219) has no use for it. */
export function siteCheats(hillSite = HILL_SITE): readonly CheatId[] {
  return hillSite ? CHEATS.filter((id) => id !== "mirror") : CHEATS;
}

/** What's going on under the menu: free skate, a 2-minute run, or a challenge (#47). */
export type PauseMode = "free" | "run" | "challenge";

/** During a challenge: go again or give up, and the lists. No map, run or demo switching mid-challenge. */
export const CHALLENGE_PAUSE_ITEMS: PauseItem[] = [
  { action: "resume", label: "Resume" },
  { action: "retry", label: "Retry challenge" },
  { action: "quit", label: "Quit challenge" },
  { action: "goals", label: "Challenges" },
  { action: "gaps", label: "Gap list" },
  { action: "settings", label: "Settings" },
  { action: "controls", label: "Controls" },
  { action: "lobby", label: "Back to lobby" },
];

/**
 * The items on a screen of the menu. `hubs` are the open hubs' tiers: in
 * free skate the Challenges screen has a "Go to hub" row for each (#87).
 */
export function pauseItems(screen: PauseScreen, mode: PauseMode = "free", hubs: readonly Tier[] = [], controls: ControlRows = ALL_CONTROL_ROWS): PauseItem[] {
  if (screen === "main") {
    if (mode === "challenge") return CHALLENGE_PAUSE_ITEMS;
    // In free skate the goal list is the Challenges list: markers to skate to.
    return siteItems(mode === "free" ? PAUSE_ITEMS.map((i) => (i.action === "goals" ? { ...i, label: "Challenges" } : i)) : PAUSE_ITEMS);
  }
  // The gap and goal lists are read-only (PauseOverlay draws them): Back, and in free skate a row per open hub.
  if (screen === "goals" && mode === "free" && hubs.length)
    return [...hubs.map((t): PauseItem => ({ action: `hub:${t}`, label: `Go to hub: ${hubName(t)}` })), { action: "back", label: "Back" }];
  if (screen === "gaps" || screen === "goals") return [{ action: "back", label: "Back" }];
  // PauseOverlay lists each day's notes under its row.
  if (screen === "news") return [...RELEASE_NOTES.map((d): PauseItem => ({ action: `news:${d.date}`, label: dayHeading(d.date) })), { action: "back", label: "Back" }];
  // PauseOverlay shows each screen's current values, the volume and the bindings.
  const back: PauseItem = { action: "back", label: "Back" };
  if (screen === "settings")
    return [
      ...SETTINGS_SCREENS.map((g): PauseItem => ({ action: `group:${g}`, label: SETTINGS_SCREEN_LABELS[g] })),
      { action: "cheats", label: "Cheats" },
      back,
    ];
  if (screen === "soundCamera") return [{ action: "volume+", label: "Volume" }, ...groupRows(screen), back];
  if (screen === "look" || screen === "effects") return [...groupRows(screen), back];
  if (screen === "input") {
    const rows: PauseItem[] = [];
    if (controls.touch) {
      rows.push(
        { action: "touchMode+", label: "Touch controls" },
        { action: "touchSize+", label: "Button size" },
        { action: "touchSteer+", label: "Steering sensitivity" },
        { action: "touchOpacity+", label: "Button opacity" },
        { action: "touchHand", label: "Left-handed" },
      );
    }
    rows.push({ action: "specials", label: "Specials" });
    if (controls.buttons) {
      rows.push(...REMAPPABLE.map((a): PauseItem => ({ action: `bind:${a}`, label: ACTION_LABELS[a] })), { action: "resetControls", label: "Reset controls" });
    }
    return [...rows, back];
  }
  if (screen === "specials")
    return [
      // The board's by name, then each other ride's (#138) under its name.
      ...RIDE_SPECIALS.map(({ ride, trick: s }): PauseItem => ({
        action: `special:${s.name}`,
        label: ride === "board" ? s.name : `${RIDES[ride].name}: ${s.name}`,
      })),
      { action: "resetSpecials", label: "Reset specials" },
      back,
    ];
  // PauseOverlay adds each choice's line (a style's pitch lives with the look, in render/styles) and marks the current one.
  if (isListKey(screen)) {
    const labels: Readonly<Record<string, string>> = SETTING_ROWS[screen].labels;
    return [...SETTING_CHOICES[screen].map((id): PauseItem => ({ action: pickAction(screen, id), label: labels[id]! })), back];
  }
  if (screen === "cheats")
    return [...siteCheats().map((id): PauseItem => ({ action: `cheat:${id}`, label: CHEAT_LABELS[id], detail: CHEAT_DETAILS[id] })), { action: "back", label: "Back" }];
  if (screen === "stats") {
    return [
      ...STAT_IDS.map((id): PauseItem => ({ action: `stat:${id}`, label: STAT_INFO[id].label, detail: STAT_INFO[id].detail })),
      { action: "back", label: "Back" },
    ];
  }
  if (screen === "skater") {
    return [
      ...SKATERS.map((s): PauseItem => ({ action: `skater:${s.id}`, label: s.name, detail: s.bio })),
      { action: "back", label: "Back" },
    ];
  }
  return [
    ...MAP_CATALOG.map((m): PauseItem => ({ action: `map:${m.id}`, label: m.name, detail: m.blurb })),
    { action: "back", label: "Back" },
  ];
}

/** A Settings screen's pick-one rows, in table order. */
function groupRows(group: SettingGroup): PauseItem[] {
  return CHOICE_KEYS.filter((k) => SETTING_ROWS[k].group === group).map((k) => ({ action: choiceRowAction(k), label: SETTING_ROWS[k].label }));
}

/** The screen a pick-one setting's row is on. */
export function screenOf(key: ChoiceKey): SettingsScreen {
  return SETTING_ROWS[key].group;
}

/** A pick-one setting's row on its Settings screen: `camera+` for a stepped one, `artStyle` for a list. */
export function choiceRowAction(key: ChoiceKey): PauseAction {
  return isListKey(key) ? key : `${key as StepKey}+`;
}

/** The pick-one setting a Settings row is for, if it's one. */
export function choiceOfRow(action: PauseAction): ChoiceKey | null {
  return CHOICE_KEYS.find((k) => choiceRowAction(k) === action) ?? null;
}

/** What a stepped row's action steps, if it is one: `camera+` steps the camera up, `camera-` down. */
export function parseStep(action: PauseAction): { key: StepKey; dir: -1 | 1 } | null {
  const m = /^(\w+)([+-])$/.exec(action);
  const key = m?.[1];
  if (!key || !(key in SETTING_ROWS) || isListKey(key)) return null;
  return { key: key as StepKey, dir: m[2] === "+" ? 1 : -1 };
}

/** A list's pick action for one of its choices. */
export function pickAction(key: ListKey, id: string): PauseAction {
  return `${SETTING_ROWS[key].pick}:${id}` as PickAction;
}

/** What a list pick action picks, if it is one. */
export function parsePick(action: PauseAction): { key: ListKey; id: string } | null {
  for (const k of CHOICE_KEYS) {
    if (!isListKey(k)) continue;
    const prefix = `${SETTING_ROWS[k].pick}:`;
    if (action.startsWith(prefix)) return { key: k, id: action.slice(prefix.length) };
  }
  return null;
}

/** Settings rows that ←/→ steps: the row's action (also X and →) and what ← does. */
export const STEP_DOWN: Partial<Record<PauseAction, PauseAction>> = {
  "volume+": "volume-",
  ...(Object.fromEntries(
    CHOICE_KEYS.filter((k) => !isListKey(k)).map((k) => [`${k}+`, `${k}-`]),
  ) as Partial<Record<PauseAction, PauseAction>>),
  "touchMode+": "touchMode-",
  "touchSize+": "touchSize-",
  "touchSteer+": "touchSteer-",
  "touchOpacity+": "touchOpacity-",
  touchHand: "touchHand",
  // A cheat is a switch like the hand row: either way flips it.
  ...(Object.fromEntries(CHEATS.map((id) => [`cheat:${id}`, `cheat:${id}`])) as Partial<Record<PauseAction, PauseAction>>),
};

export class PauseMenu {
  open = false;
  screen: PauseScreen = "main";
  mode: PauseMode = "free";
  index = 0;
  /** The open hubs' tiers while the Challenges screen is up (#87): its "Go to hub" rows. */
  hubs: readonly Tier[] = [];
  /** Which control rows this device gets on Settings > Controls (#126); Game keeps it current. */
  controls: ControlRows = ALL_CONTROL_ROWS;
  private lastY = 0;
  private lastX = 0;

  show(mode: PauseMode = "free"): void {
    this.open = true;
    this.mode = mode;
    this.screen = "main";
    this.index = 0;
    this.lastY = 0;
    this.lastX = 0;
  }

  hide(): void {
    this.open = false;
  }

  /** Switches to the map list, highlighting the map you're on. */
  openMaps(currentId: string): void {
    this.screen = "maps";
    this.index = Math.max(0, MAP_CATALOG.findIndex((m) => m.id === currentId));
  }

  /** Switches to the gap list for the map you're on. */
  openGaps(): void {
    this.screen = "gaps";
    this.index = 0;
  }

  /** Switches to What's new (#159), on the newest day. */
  openNews(): void {
    this.screen = "news";
    this.index = 0;
  }

  /** Switches to the goal list (the run's, or the map's when there's no run), with a "Go to hub" row per open hub's tier. */
  openGoals(hubs: readonly Tier[] = []): void {
    this.screen = "goals";
    this.hubs = hubs;
    this.index = 0;
  }

  /** Switches to the Stats screen, on the first stat. */
  openStats(): void {
    this.screen = "stats";
    this.index = 0;
  }

  /** Switches to the Skater screen, highlighting who you're riding. */
  openSkater(currentId: string): void {
    this.screen = "skater";
    this.index = Math.max(0, SKATERS.findIndex((s) => s.id === currentId));
  }

  /** Switches to the Settings screen, on its first row. */
  openSettings(): void {
    this.screen = "settings";
    this.index = 0;
  }

  /** Switches to one of Settings' screens, on its first row. */
  openGroup(group: SettingsScreen): void {
    this.screen = group;
    this.index = 0;
  }

  /** Switches to a setting's pick list, highlighting the choice in use. Moving through it changes nothing. */
  openList(key: ListKey, current: string): void {
    this.screen = key;
    this.index = Math.max(0, (SETTING_CHOICES[key] as readonly string[]).indexOf(current));
  }

  openArtStyles(current: ArtStyleId): void {
    this.openList("artStyle", current);
  }

  openQualities(current: Quality): void {
    this.openList("quality", current);
  }

  /** Switches to the Specials list (#135), on the first special. */
  openSpecials(): void {
    this.screen = "specials";
    this.index = 0;
  }

  /** Takes in where the stick is without acting on it, so a stick held from waiting on special taps doesn't move the highlight. */
  holdStick(f: InputFrame): void {
    this.lastY = f.moveY > 0.5 ? 1 : f.moveY < -0.5 ? -1 : 0;
    this.lastX = f.moveX > 0.5 ? 1 : f.moveX < -0.5 ? -1 : 0;
  }

  /** Switches to the Cheats list, on the first cheat. */
  openCheats(): void {
    this.screen = "cheats";
    this.index = 0;
  }

  /**
   * Back a level, on the item that opened this screen: from a pick list to
   * its Settings screen, from a Settings screen or the Cheats to Settings,
   * from anywhere else to the main screen.
   */
  back(): void {
    const from = this.screen;
    // A pick list goes back to its screen, on its row.
    if (isListKey(from)) return this.backTo(screenOf(from), from);
    if (isSettingsScreen(from)) return this.backTo("settings", `group:${from}`);
    if (from === "cheats") return this.backTo("settings", "cheats");
    if (from === "specials") return this.backTo("input", "specials");
    this.backTo("main", from as PauseAction);
  }

  /** Switches to `screen`, on the row whose action is `row` (or the first). */
  private backTo(screen: PauseScreen, row: PauseAction): void {
    this.screen = screen;
    this.index = Math.max(0, pauseItems(screen, this.mode, this.hubs, this.controls).findIndex((i) => i.action === row));
  }

  /**
   * One frame of input while the menu is open: up/down moves (one step per
   * push, not per frame), X picks, Start resumes, O backs out a level. On a
   * stat row, right (like X) spends a point and left takes one back; on the
   * stepped settings rows (`STEP_DOWN`), right (like X) steps up and left down. Returns
   * the action to run, if any.
   */
  handle(f: InputFrame): PauseAction | null {
    const items = pauseItems(this.screen, this.mode, this.hubs, this.controls);
    const y = f.moveY > 0.5 ? 1 : f.moveY < -0.5 ? -1 : 0;
    if (y !== 0 && y !== this.lastY) {
      const n = items.length;
      this.index = (this.index - y + n) % n; // stick up moves up the list
    }
    this.lastY = y;
    const x = f.moveX > 0.5 ? 1 : f.moveX < -0.5 ? -1 : 0;
    const pushed = x !== 0 && x !== this.lastX;
    this.lastX = x;
    const item = items[this.index]!;
    if (pushed && item.action.startsWith("stat:")) return x > 0 ? item.action : `unstat:${item.action.slice("stat:".length) as StatId}`;
    if (pushed && item.action.startsWith("special:")) return `special${x > 0 ? "+" : "-"}:${item.action.slice("special:".length)}`;
    const down = STEP_DOWN[item.action];
    if (pushed && down) return x > 0 ? item.action : down;
    if (f.pressed.pause) return "resume";
    if (f.pressed.grab) return this.screen === "main" ? "resume" : "back";
    if (f.pressed.ollie) return item.action;
    return null;
  }
}
